import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, interval, map } from 'rxjs';

@customElement('ils-countdown')
export class ILSCountdown extends ChemistryLitElement {
  @property({ attribute: false }) datetime = new RXController<Maybe<DateTime>>(this, undefined);

  @state() private readonly _timer = new RXController(this, interval(1000));
  @state() private _timerExceededEventThrowed = false;
  @state() private readonly _millis = new RXController(
    this,
    combineLatest([this.datetime.observable.pipe(filter(Boolean)), this._timer.observable.pipe(filter(Boolean))]).pipe(
      map(([product, timer]) => {
        if (this._timerExceededEventThrowed) return;
        const remainingMillis = this._remainingMillis(product);
        if (remainingMillis < 1000) {
          if (remainingMillis < 0) {
            this.dispatchEvent(new Event('timer-exceeded'));
            this._timerExceededEventThrowed = true;
          }
          return 0;
        }
        return remainingMillis;
      }),
    ),
  );

  override render() {
    if (!this.datetime.value) return nothing;

    const timeData = this._convertMillis(this._millis.value ?? this._remainingMillis(this.datetime.value));

    return html`
      <x-grid space="xs" columns="4">
        <x-h4 small bold class="counter-number" center>${this._fillTimeWithZeros(timeData.days)}</x-h4>
        <x-h4 small bold class="counter-number" center>${this._fillTimeWithZeros(timeData.hours)}</x-h4>
        <x-h4 small bold class="counter-number" center>${this._fillTimeWithZeros(timeData.minutes)}</x-h4>
        <x-h4 small bold class="counter-number" center>${this._fillTimeWithZeros(timeData.seconds)}</x-h4>
        <x-text small class="counter-header" center> Tage </x-text>
        <x-text small class="counter-header" center> Std </x-text>
        <x-text small class="counter-header" center> Min </x-text>
        <x-text small class="counter-header" center> Sek </x-text>
      </x-grid>
    `;
  }

  private _remainingMillis(datetime: DateTime) {
    return datetime.toMillis() - DateTime.now().toMillis();
  }

  private _fillTimeWithZeros(data: number) {
    return data < 10 ? `${data}` : data;
  }

  private _convertMillis(millis) {
    const seconds = Math.floor(millis / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;

    return {
      days,
      hours: remainingHours,
      minutes: remainingMinutes,
      seconds: remainingSeconds,
    };
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '.counter-number': {
          padding: '4px',
          background: this.colors.accent,
          borderRadius: this.sizes.borderRadius,
          opacity: '1',
          color: '#fff',
          textAlign: 'center',
        },
        '.counter-header': {
          color: this.colors.tone.secondaryText,
        },
      },
    ];
  }
}
